import { useState } from 'react';
import { SheetB } from '../SheetB';

/**
 * PRE-6 in B's shell — naming the board a past match locked, so it joins the template library
 * the next setup offers (build unit B-6; spec §2.4's "save-board-as-template for past matches").
 *
 * The name is pre-filled with "opponent · date" — a template name is a label, and that is the
 * one a user would type anyway. The engine refuses a blank too (`ERR_TEMPLATE_NAME_REQUIRED`);
 * the disabled Save is what the user reads, the rejection is what makes it true.
 */

export interface SaveTemplateSheetBProps {
  /** What the template will be *of*, so the user is naming something they can see. */
  readonly opponent: string;
  readonly date: string;
  readonly entryCount: number;
  readonly busy: boolean;
  /** A rejection sentence from the engine (`DispatchResult.message`). */
  readonly problem: string | null;
  readonly onSave: (name: string) => void;
  readonly onClose: () => void;
}

export function SaveTemplateSheetB({
  opponent,
  date,
  entryCount,
  busy,
  problem,
  onSave,
  onClose,
}: SaveTemplateSheetBProps) {
  const [draft, setDraft] = useState(`${opponent} · ${date}`);
  const blank = draft.trim() === '';

  return (
    <SheetB
      titleId="oppfile-template-title"
      title="Save this board as a template"
      testId="oppfile-template-sheet"
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="sheet-b__primary"
            data-testid="oppfile-template-save"
            disabled={blank || busy}
            onClick={() => {
              onSave(draft);
            }}
          >
            Save
          </button>
          <button
            type="button"
            className="sheet-b__secondary"
            data-testid="oppfile-template-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
        </>
      }
    >
      <label className="sheet-b__label" htmlFor="oppfile-template-field">
        Template name
      </label>
      <input
        id="oppfile-template-field"
        className="sheet-b__field"
        data-testid="oppfile-template-field"
        type="text"
        autoComplete="off"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
      />
      <p className="sheet-b__hint" data-testid="oppfile-template-count">
        {entryCount} pattern{entryCount === 1 ? '' : 's'} from this board. It joins your template
        library for the next match setup.
      </p>
      {problem !== null && (
        <p className="sheet-b__problem" data-testid="oppfile-template-problem" role="status">
          {problem}
        </p>
      )}
    </SheetB>
  );
}
